// Eskom PARCC — Working Paper capture/edit modal (review fields).
//
// The lightweight write counterpart to the read-only Working Papers row. An
// assigned reviewer (or an admin) opens this from the Working Papers table to set
// a transaction's Review Status, Working Paper Notes and Compliance level, then
// saves — explicit Save only, no autosave.
//
// This is the SIMPLE review-field capture; the full Compliance Evaluation Sheet
// (per-criterion checklist) remains the CES modal (Task 19), reachable from the
// same row. Save sequence, mirroring TransactionEditor / CesModal:
//   1. `checkRowVersion(referenceNo, frozenRowVersion)` — re-read the current
//      server rowVersion (frozen at open) and compare; on conflict, surface and
//      close so the fresh state is re-read (never overwrite a newer change).
//   2. `useUpsertWorkingPaper` with the review fields + capturedByEmail, then
//      `useUpsertTransaction` mirroring the review status onto the transaction
//      (the DUAL-WRITE — so register + allocation agree with the working paper).
// Both mutation hooks OWN their error toast (onError); a rejection has already
// surfaced exactly one error, so the handler does not toast again and does not
// close (the modal stays open for a retry). Both buttons disable while in flight.
//
// Permission is the caller's responsibility (canEditTransaction); the page only
// mounts this for a row the current user may edit. Styling reuses the existing
// `.modal-*` / `.detail-edit-grid` classes — no new CSS.
import { useMemo, useState } from 'react';
import { Button, Select, useToast } from '../../components/ui';
import { REVIEW_STATUS_OPTIONS, TRANSACTION_COMPLIANCE_LEVELS } from '../../domain/enums';
import { checkRowVersion } from '../../lib/concurrency';
import { adminApi } from '../../lib/adminApi';
import { useUpsertWorkingPaper, useUpsertTransaction, useReplaceFindings } from '../../hooks';
import { diffFields, changesNote, TRANSACTION_FIELD_LABELS } from '../../lib/fieldDiff';
import type { Transaction } from '../../data/types';
import type { WorkingPaperRead, FindingRead } from '../../lib/db';
import { FindingEditorCard } from '../findings/FindingEditorCard';
import {
  type FindingDraft,
  applySuggestion,
  draftsToInsertInputs,
  findingToDraft,
  newFindingDraft,
  txContextOf,
} from '../transactions/editTransaction';
import {
  buildWorkingPaperInput,
  buildReviewStatusTxPatch,
  buildComplianceTxPatch,
  resolveComplianceForSave,
  type WorkingPaperDraft,
} from './editWorkingPaper';
import { rollupGapStatusFromFindings } from '../../domain/gapClosure';

export function WorkingPaperEditor({
  tx,
  wp,
  findings,
  capturedByEmail,
  onClose,
}: {
  /** The transaction being captured against (the row's allocation). */
  tx: Transaction;
  /** Its existing working paper, if one has been captured (seeds the form). */
  wp: WorkingPaperRead | undefined;
  /**
   * The transaction's existing finding lines (already filtered by referenceNo).
   * MUST be the live set: Save replaces ALL findings for the tx (delete-then-
   * reinsert), so seeding from a stale/empty set would wipe existing findings.
   */
  findings: readonly FindingRead[];
  /** The signed-in user's email, stamped onto the working paper as capturer. */
  capturedByEmail: string;
  /** Leave edit mode (after a successful save, or on cancel). */
  onClose: () => void;
}): React.JSX.Element {
  const { show } = useToast();
  const upsertWorkingPaper = useUpsertWorkingPaper();
  const upsertTransaction = useUpsertTransaction();
  const replaceFindings = useReplaceFindings();

  // Freeze the rowVersion the editor opened against — the concurrency precondition
  // checked at Save. Captured once at mount (mirrors TransactionEditor / CesModal):
  // a background refetch can advance the same open tx's rowVersion while the modal
  // stays mounted, so tracking it live would defeat the guard (new-vs-new passes).
  const [loadedRowVersion] = useState(tx.rowVersion);

  // Seed the draft from the working paper, falling back to the transaction's own
  // captured values (the read layer surfaces these the same way the list does). The
  // compliance seed is the VERDICT OF RECORD — the strict-rule override the register
  // treats as authoritative — before the paper's own level: a verdict given from the
  // transaction editor before 29 Sep 2026 was written to the override only, and seeding
  // from the stale paper level would dual-write it back over the override on the next
  // save (Lindiwe, 4600080858). `||` so a blank never masks a set value.
  const seededCompliance = useState(
    () => tx.complianceLevelOverride || wp?.complianceLevel || tx.complianceLevel || '',
  )[0];
  const [draft, setDraft] = useState<WorkingPaperDraft>(() => ({
    reviewStatus: wp?.reviewStatus ?? tx.reviewStatus ?? '',
    workingPaperNotes: wp?.workingPaperNotes ?? tx.workingPaperNotes ?? '',
    complianceLevel: seededCompliance,
    reviewerName: wp?.reviewerName ?? '',
  }));

  // Finding drafts (local working copy of the lines) — the SAME capture surface as
  // the transaction editor, so a finding recorded on the working paper reaches the
  // Findings Register and anchors to the rulebook (owner feedback 2). Seeded from
  // the tx's existing findings so Save (replace-per-tx) preserves them.
  const ctx = useMemo(() => txContextOf(tx), [tx]);
  const [drafts, setDrafts] = useState<FindingDraft[]>(() => findings.map(findingToDraft));
  // Frozen projection of the seeded lines: at Save, the R3 #2 recompute of the compliance
  // level runs ONLY when the lines differ from this (a lifecycle edit happened here) — a
  // notes-only save never rewrites the verdict (see resolveComplianceForSave).
  const seededFindingInputs = useState(
    () => JSON.stringify(draftsToInsertInputs(findings.map(findingToDraft))),
  )[0];

  const saving =
    upsertWorkingPaper.isPending || upsertTransaction.isPending || replaceFindings.isPending;

  function patchDraft(key: string, p: Partial<FindingDraft>): void {
    setDrafts((ds) => ds.map((d) => (d.key === key ? { ...d, ...p } : d)));
  }
  function onTextChange(key: string, text: string): void {
    setDrafts((ds) => ds.map((d) => (d.key === key ? applySuggestion(d, text, ctx) : d)));
  }
  function toggleAnchor(key: string, anchor: string, checked: boolean): void {
    setDrafts((ds) =>
      ds.map((d) => {
        if (d.key !== key) return d;
        const set = new Set(d.anchors);
        if (checked) set.add(anchor);
        else set.delete(anchor);
        return { ...d, anchors: [...set] };
      }),
    );
  }
  function addFinding(): void {
    setDrafts((ds) => [...ds, newFindingDraft()]);
  }
  function deleteFinding(key: string): void {
    setDrafts((ds) => ds.filter((d) => d.key !== key));
  }

  function patch(p: Partial<WorkingPaperDraft>): void {
    setDraft((d) => ({ ...d, ...p }));
  }

  async function handleSave(): Promise<void> {
    // 1. Concurrency guard — narrow the last-write-wins window. Runs BEFORE any
    //    write, so a throw here means nothing was written; surface it (no mutation
    //    hook is involved yet) and leave the modal open for a retry.
    let check;
    try {
      check = await checkRowVersion(tx.referenceNo, loadedRowVersion);
    } catch (error) {
      show(
        error instanceof Error ? error.message : 'Could not verify the transaction. Please retry.',
        'error',
      );
      return;
    }
    if (!check.ok) {
      show(
        check.current === null
          ? 'This transaction no longer exists. Refreshing…'
          : 'This transaction was changed by someone else. Refreshing — please reapply your edits.',
        'error',
      );
      onClose(); // re-reads from the (invalidated/fresh) cache
      return;
    }

    // 2. Write phase. Each mutation hook OWNS its own error toast (onError), so a
    //    rejection has already surfaced exactly one error — do not toast again, and
    //    do not close (the modal stays open so the user can retry with edits intact).
    try {
      // R3 #2 propagation: resolve the compliance to persist — the reviewer's
      // explicit choice, or the rollup recomputed from the findings' lifecycle
      // states (closing a gap updates the tx + dashboard + reports).
      const findingInputs = draftsToInsertInputs(drafts);
      const findingsChanged = JSON.stringify(findingInputs) !== seededFindingInputs;
      const compliance = resolveComplianceForSave(
        draft.complianceLevel,
        seededCompliance,
        findingInputs,
        findingsChanged,
      );
      // Roll up the transaction's GAP STATUS from the findings' lifecycle, so the
      // persisted Transaction.gapsStatus (what the Transactions filter, exports and
      // editor read) matches the dashboard's Gaps Open/Closed. A rollup of '' — no
      // gaps — leaves the field unchanged (DC treats an omitted var as a no-op).
      const gapsStatus = rollupGapStatusFromFindings(findingInputs) || undefined;

      // 2a. Persist the working paper (the captured review fields + capturer).
      await upsertWorkingPaper.mutateAsync({
        referenceNo: tx.referenceNo,
        input: buildWorkingPaperInput({ ...draft, complianceLevel: compliance }, capturedByEmail),
      });

      // 2b. Mirror the review status AND the resolved compliance onto the
      //     transaction (the dual-write), so the register, allocation board and
      //     dashboard agree with the working paper. Skipped when there is
      //     nothing to mirror (an empty upsert would only bump the row).
      const statusPatch = buildReviewStatusTxPatch(tx.referenceNo, draft);
      const compliancePatch = buildComplianceTxPatch(tx.referenceNo, compliance);
      if (statusPatch || compliancePatch || gapsStatus) {
        const txInput = {
          referenceNo: tx.referenceNo,
          ...(gapsStatus ? { gapsStatus } : {}),
          ...(statusPatch ?? {}),
          ...(compliancePatch ?? {}),
        };
        const txChanges = diffFields(
          tx as unknown as Record<string, unknown>,
          txInput,
          TRANSACTION_FIELD_LABELS,
        );
        await upsertTransaction.mutateAsync({
          input: txInput,
          audit: { note: changesNote(txChanges), changes: txChanges },
        });
      }

      // 2c. Replace-per-tx for the findings — the SAME path the transaction editor
      //     uses (delete-then-reinsert). This is what makes working-paper findings
      //     reach the register (owner feedback 2); the referenceNo is stamped at the
      //     write layer, and the classifier-derived anchors/compliance/references
      //     are carried on each draft. Drafts seeded from the live finding set, so
      //     existing findings are preserved unless the reviewer removed them.
      await replaceFindings.mutateAsync({
        referenceNo: tx.referenceNo,
        findings: findingInputs,
      });
    } catch {
      return; // already toasted by the rejecting hook's onError; stay open
    }

    // Best-effort branded notification: when the reviewer marks the paper as
    // Reviewed, tell the admin who allocated it (recipient resolved server-side
    // from the transaction). Never blocks or fails the (already-saved) review.
    if (draft.reviewStatus === 'Reviewed') {
      void adminApi.notifyReviewComplete(tx.referenceNo).catch(() => undefined);
    }

    show('Working paper saved.', 'success');
    onClose();
  }

  /**
   * Send a reviewed/pending paper back for revision: move it to "In Progress" and
   * notify the assigned reviewer (recipient resolved server-side). The note is
   * optional. Best-effort email; the status write is the source of truth.
   */
  async function handleRequestRevision(): Promise<void> {
    const note = window.prompt('Add a note for the reviewer (optional):', '') ?? '';
    try {
      const txInput = { referenceNo: tx.referenceNo, reviewStatus: 'In Progress' };
      const txChanges = diffFields(
        tx as unknown as Record<string, unknown>,
        txInput,
        TRANSACTION_FIELD_LABELS,
      );
      await upsertTransaction.mutateAsync({
        input: txInput,
        audit: { note: changesNote(txChanges), changes: txChanges },
      });
    } catch {
      return; // hook's onError already toasted
    }
    void adminApi.notifyRevisionRequest(tx.referenceNo, note).catch(() => undefined);
    show('Revision requested — the reviewer has been notified.', 'success');
    onClose();
  }

  return (
    <div
      className="modal-backdrop open"
      role="dialog"
      aria-modal="true"
      aria-labelledby="wpEditHeading"
    >
      <div className="modal-card">
        <div className="modal-head">
          <div>
            <h3 id="wpEditHeading" style={{ margin: 0 }}>
              {tx.referenceNo}
            </h3>
            <div className="small">Capture the working-paper review fields</div>
          </div>
          <div className="actions">
            <Button variant="ghost" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            {tx.assignedToEmail && (
              <Button
                variant="ghost"
                onClick={() => void handleRequestRevision()}
                disabled={saving}
              >
                Request revision
              </Button>
            )}
            <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>

        <div className="detail-edit-grid">
          <label>
            Review status
            <Select
              ariaLabel="Review status"
              value={draft.reviewStatus}
              disabled={saving}
              onChange={(v) => patch({ reviewStatus: v })}
              options={[
                { value: '', label: '—' },
                ...REVIEW_STATUS_OPTIONS.map((o) => ({ value: o, label: o })),
              ]}
            />
          </label>
          <label>
            Compliance level
            <Select
              ariaLabel="Compliance level"
              value={draft.complianceLevel}
              disabled={saving}
              onChange={(v) => patch({ complianceLevel: v })}
              options={[
                { value: '', label: '—' },
                ...TRANSACTION_COMPLIANCE_LEVELS.map((o) => ({ value: o, label: o })),
              ]}
            />
          </label>
        </div>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span className="small">
            Reviewer name — the person who actually performed the review, when a coordinator
            captures on their behalf (owner feedback #8). Optional.
          </span>
          <input
            value={draft.reviewerName}
            disabled={saving}
            onChange={(e) => patch({ reviewerName: e.target.value })}
            placeholder="e.g. Iris M. (actual reviewer)"
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span className="small">
            Working paper notes — narrative only. Anything that should reach the Findings Register
            and dashboard must be added under Findings below (findings are global; notes are not).
          </span>
          <textarea
            value={draft.workingPaperNotes}
            disabled={saving}
            onChange={(e) => patch({ workingPaperNotes: e.target.value })}
            placeholder="Record the working-paper review notes…"
          />
        </label>

        {/* Finding capture — the same rulebook-anchored lines as the transaction
            editor, so findings recorded here populate the Findings Register. */}
        <div style={{ marginTop: 16 }}>
          <div className="section-head" style={{ marginBottom: 10 }}>
            <div style={{ fontWeight: 700 }}>
              Findings{drafts.length ? ` (${drafts.length})` : ''}
            </div>
            <Button onClick={addFinding} disabled={saving}>
              Add finding
            </Button>
          </div>
          {drafts.length === 0 ? (
            <div className="empty">
              No findings. Use “Add finding” to record one — it is anchored to the rulebook and
              added to the Findings Register on save.
            </div>
          ) : (
            drafts.map((d) => (
              <FindingEditorCard
                key={d.key}
                draft={d}
                disabled={saving}
                onText={(text) => onTextChange(d.key, text)}
                onPatch={(p) => patchDraft(d.key, p)}
                onToggleAnchor={(anchor, checked) => toggleAnchor(d.key, anchor, checked)}
                onDelete={() => deleteFinding(d.key)}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
